import type { HubPlanetDto } from '@aiai/shared';
import { projectHref } from '../lib/projectNav';
import { workspacePath } from '../lib/workspacePath';

/** The planet's home page, by pretty code when it has one, in ITS workspace. */
export function planetHref(p: HubPlanetDto): string {
  return workspacePath(p.workspace.slug, projectHref(p.kind, p.code ?? p.id));
}

export interface NeedsYouItem {
  key: string;
  projectLabel: string;
  label: string;
  href: string;
}

export interface NeedsYouInput {
  id: string;
  label: string;
  href: string;
  kind: 'repo' | 'prototype';
  failedIssueCount: number;
  hasGithubCredential: boolean;
  liveStatus: string | null;
}

/** The needs-you rules for one project — shared by the planets and the Needs you list. */
export function needsYouFor(p: NeedsYouInput): NeedsYouItem[] {
  const base = { projectLabel: p.label, href: p.href };
  const items: NeedsYouItem[] = [];
  if (p.failedIssueCount > 0) {
    const n = p.failedIssueCount;
    // Deep-links to the board's Failed filter (the page reads `?issues=`).
    items.push({ ...base, href: `${p.href}?issues=failed`, key: `${p.id}:gave_up`, label: `${n} issue${n === 1 ? '' : 's'} gave up after retries` });
  }
  if (p.kind === 'repo' && !p.hasGithubCredential) {
    items.push({ ...base, key: `${p.id}:no_token`, label: 'No GitHub token' });
  }
  if (p.liveStatus === 'failed') {
    items.push({ ...base, key: `${p.id}:deploy_failed`, label: 'Deploy failed' });
  }
  return items;
}

export function needsYouItems(planets: HubPlanetDto[]): NeedsYouItem[] {
  return planets.flatMap((p) =>
    needsYouFor({
      id: p.id,
      label: p.code ?? p.name,
      href: planetHref(p),
      kind: p.kind,
      failedIssueCount: p.failedIssueCount,
      hasGithubCredential: p.hasGithubCredential,
      liveStatus: p.live?.status ?? null,
    }),
  );
}

export function planetNeedsYou(p: HubPlanetDto): boolean {
  return needsYouItems([p]).length > 0;
}
